/* eslint-disable prettier/prettier */
import React, {useCallback, useMemo, useState} from 'react';
import {Text} from '../text';
import {Icon} from '../icons';
import {Flag} from '../flag/Flag';
import {Layout} from '../layout';
import {Ruler} from '../ruler';
import {Dropdown} from '../dropdown';
import {PhoneInputProps} from './types';
import {DropdownState} from '../dropdown/types';
import {Country} from '../type/country';
import {Input, LeftEnhancer} from '../input';
import {parseNationalNumber} from './functions/parseNationalNumber';
import {Spacer} from '../spacer';

// eslint-disable-next-line @typescript-eslint/no-var-requires
const allCountries = require('../../assets/countries/countries.json') as Country[];

const formatNumber = (countryCode: string, number = '') => {
  const {nationalNumberFormatted} = parseNationalNumber(countryCode, number);
  return nationalNumberFormatted;
};

export const PhoneInput: React.FC<PhoneInputProps> = ({
  size = 'Default',
  initialCountryIso2,
  initialNumber,
  countries,
  favouritesIso2,
  onChange,
}) => {
  const [dropdownState, setDropdownState] = useState<DropdownState>(
    'collapsed',
  );

  const [nationalNumber, setNationalNumber] = useState(() =>
    formatNumber(initialCountryIso2, initialNumber),
  );

  const [selectedCountryIso2, setSelectedCountryIso2] = useState(
    initialCountryIso2,
  );

  const resolvedCountries = useMemo(() => countries || allCountries, [
    countries,
  ]);

  const countryItems = useMemo(
    () => resolvedCountries.map(c => ({id: c.iso2, ...c})),
    [resolvedCountries],
  );

  const selectedCountry = useMemo(
    () => countryItems.find(c => c.iso2 === selectedCountryIso2),
    [countryItems, selectedCountryIso2],
  );

  const handleToggleDropdown = useCallback(
    () =>
      setDropdownState(state =>
        state === 'collapsed' ? 'expanded' : 'collapsed',
      ),
    [],
  );

  const handleCollapseDropdown = useCallback(
    () => setDropdownState('collapsed'),
    [],
  );

  const handleChange = useCallback(
    (newIso2: string | undefined, newNumber: string | undefined) => {
      if (newIso2 !== undefined) {
        setSelectedCountryIso2(newIso2);
      }

      const currentIso2 = newIso2 || selectedCountryIso2;

      const {
        isValid,
        nationalNumberFormatted,
        internationalNumberFormatted,
        number,
      } = parseNationalNumber(currentIso2, newNumber ?? nationalNumber ?? '');

      if (newNumber !== undefined) {
        setNationalNumber(nationalNumberFormatted);
      }

      onChange({
        isValid,
        countryCodeIso2: selectedCountryIso2,
        nationalNumberFormatted,
        internationalNumberFormatted,
        number,
      });
    },
    [nationalNumber, onChange, selectedCountryIso2],
  );

  const handleCountrySelected = useCallback(
    (iso2: string) => {
      handleChange(iso2, undefined);
      handleToggleDropdown();
    },
    [handleToggleDropdown, handleChange],
  );

  const handleNumberChanged = useCallback(
    (value: string) => {
      handleChange(undefined, value);
    },
    [handleChange],
  );

  const renderItem = useCallback(
    (id: string | number, index: number) => {
      const isSelected = id === selectedCountryIso2;
      return (
        <Layout
          onPress={() => handleCountrySelected(resolvedCountries[index].iso2)}
          hover
          justify="space-between"
          align="center"
          paddingHorizontal="12"
          paddingVertical="8"
          backgroundColor={
            isSelected ? 'background_mono_tertiary' : 'background_mono_primary'
          }>
          <Layout flex align="center" justify="space-between">
            <Layout paddingRight="14">
              <Flag countryCode={resolvedCountries[index].iso2} />
            </Layout>
            <Layout flex={1}>
              <Text
                align="left"
                textColor="content_mono_primary"
                variant={
                  isSelected ? 'paragraph_14_emphasis' : 'paragraph_14_default'
                }>
                {resolvedCountries[index].name}
              </Text>
              <Layout paddingLeft="4">
                <Text
                  textColor="content_mono_secondary"
                  variant="paragraph_14_default">
                  {'+' + resolvedCountries[index].code}
                </Text>
              </Layout>
            </Layout>
          </Layout>
          <Icon name="checkmarkSmall" transparent={!isSelected} />
        </Layout>
      );
    },
    [handleCountrySelected, resolvedCountries, selectedCountryIso2],
  );

  const renderSeparator = useCallback(() => <Ruler padding="6" />, []);

  return (
    <Layout vertical onBlur={handleCollapseDropdown} height={44}>
      <Input
        value={nationalNumber}
        size={size}
        leftText={'+' + selectedCountry?.code}
        onChange={handleNumberChanged}
        onFocus={handleCollapseDropdown}>
        {/* Country selector with number input */}
        <LeftEnhancer>
          <Layout paddingRight="8" justify="flex-start">
            <Layout
              onPress={handleToggleDropdown}
              borderRadius="4"
              hover
              focusBorder={dropdownState === 'expanded' ? 'on' : 'off'}
              padding="4"
              backgroundColor="background_mono_tertiary">
              <Flag countryCode={selectedCountryIso2} />
              <Spacer width="4" />
              <Icon name="arrowDownSmall" />
            </Layout>
          </Layout>
        </LeftEnhancer>
      </Input>
      <Layout>
        <Dropdown
          state={dropdownState}
          items={countryItems}
          favourites={favouritesIso2}
          renderItemContent={renderItem}
          renderFavouritesSeparator={renderSeparator}
        />
      </Layout>
    </Layout>
  );
};
